import { useCallback, useMemo, useState } from 'react';
import { API } from '@gin-betting/core';
import { difference, without } from 'lodash';

import { useDidUpdate } from 'hooks';
import { CategorizerRowType } from '../../constants';

interface Input {
    isAllItemsInSportChecked?: boolean;
    isGroupWithOneItem?: boolean;
    items: API.CategorizerItem[];
    selectedTournamentIds: string[];
    topCategorizerItem: API.CategorizerItem;
    handleTournamentIdsChange: (nextTournamentIds: string[]) => void;
}

interface Output {
    activeTournamentIds: string[];
    hasAnyCheckedTournament: boolean;
    isAllTournamentsChecked: boolean;
    isSubgroupManuallyChecked: boolean;
    isTournamentManuallyChecked: boolean;
    itemTournamentIds: string[];
    handleItemCheck: (
        checkedTournamentIds: string[],
        groupType: CategorizerRowType
    ) => void;
}

const useCategorizerRowChecker = ({
    selectedTournamentIds,
    items,
    topCategorizerItem,
    handleTournamentIdsChange,
    isAllItemsInSportChecked,
    isGroupWithOneItem = false,
}: Input): Output => {
    const [
        isTournamentManuallyChecked,
        setTournamentManuallyChecked,
    ] = useState<boolean>(false);
    const [
        isSubgroupManuallyChecked,
        setSubgroupManuallyChecked,
    ] = useState<boolean>(false);

    const itemTournamentIds = useMemo(() => items.map(({ value }) => value), [
        items,
    ]);

    const isAllTournamentsChecked = useMemo(
        () => difference(itemTournamentIds, selectedTournamentIds).length === 0,
        [selectedTournamentIds, itemTournamentIds]
    );

    const hasAnyCheckedTournament =
        selectedTournamentIds.length > 0 &&
        itemTournamentIds.some(id => selectedTournamentIds.includes(id));

    const handleItemCheck = useCallback(
        (
            checkedTournamentIds: string[],
            groupType: CategorizerRowType
        ): void => {
            const allTournamentsInSport = topCategorizerItem.children.map(
                ({ value }) => value
            );

            const nextTournamentIds = getNextTournamentIds({
                allTournamentsInSport,
                checkedTournamentIds,
                groupType,
                hasAnyCheckedTournament,
                isAllItemsInSportChecked:
                    isAllItemsInSportChecked ?? isAllTournamentsChecked,
                isAllTournamentsChecked,
                isGroupWithOneItem,
                isSubgroupManuallyChecked,
                isTournamentManuallyChecked,
                itemTournamentIds,
                selectedTournamentIds,
            });

            if (groupType === CategorizerRowType.Tournament) {
                setTournamentManuallyChecked(true);
            }

            if (groupType === CategorizerRowType.Subgroup) {
                setSubgroupManuallyChecked(true);
            }

            handleTournamentIdsChange(nextTournamentIds);
        },
        [
            handleTournamentIdsChange,
            hasAnyCheckedTournament,
            isAllItemsInSportChecked,
            isAllTournamentsChecked,
            isGroupWithOneItem,
            isSubgroupManuallyChecked,
            isTournamentManuallyChecked,
            itemTournamentIds,
            selectedTournamentIds,
            topCategorizerItem,
        ]
    );

    useDidUpdate(() => {
        if (hasAnyCheckedTournament) return;

        setTournamentManuallyChecked(false);
        setSubgroupManuallyChecked(false);
    }, [hasAnyCheckedTournament]);

    const activeTournamentIds =
        isAllTournamentsChecked &&
        itemTournamentIds.length > 1 &&
        !isTournamentManuallyChecked &&
        !isSubgroupManuallyChecked
            ? []
            : selectedTournamentIds;

    return {
        itemTournamentIds,
        activeTournamentIds,
        isAllTournamentsChecked,
        hasAnyCheckedTournament,
        isTournamentManuallyChecked,
        isSubgroupManuallyChecked,
        handleItemCheck,
    };
};

interface GetNextTournamentsArgs {
    allTournamentsInSport: string[];
    checkedTournamentIds: string[];
    groupType: CategorizerRowType;
    hasAnyCheckedTournament: boolean;
    isAllItemsInSportChecked: boolean;
    isAllTournamentsChecked: boolean;
    isGroupWithOneItem: boolean;
    isSubgroupManuallyChecked: boolean;
    isTournamentManuallyChecked: boolean;
    itemTournamentIds: string[];
    selectedTournamentIds: string[];
}

function getNextTournamentIds({
    itemTournamentIds,
    allTournamentsInSport,
    checkedTournamentIds,
    selectedTournamentIds,
    isAllTournamentsChecked,
    hasAnyCheckedTournament,
    groupType,
    isTournamentManuallyChecked,
    isSubgroupManuallyChecked,
    isAllItemsInSportChecked,
    isGroupWithOneItem,
}: GetNextTournamentsArgs): string[] {
    const isGroupCheckClicked = groupType === CategorizerRowType.Subgroup;
    const hasSingleTournament = itemTournamentIds.length === 1;

    if (isAllItemsInSportChecked) {
        if (
            allTournamentsInSport.length === 1 ||
            (isGroupCheckClicked && isGroupWithOneItem)
        ) {
            return without(selectedTournamentIds, ...checkedTournamentIds);
        }

        const isAllTournamentCheckedManually =
            isAllTournamentsChecked &&
            (isSubgroupManuallyChecked || isTournamentManuallyChecked);

        if (!isAllTournamentCheckedManually) {
            return without(
                selectedTournamentIds,
                ...allTournamentsInSport
            ).concat(checkedTournamentIds);
        }

        const itemsToRemove = isTournamentManuallyChecked
            ? checkedTournamentIds
            : without(
                  isGroupCheckClicked
                      ? allTournamentsInSport
                      : itemTournamentIds,
                  ...checkedTournamentIds
              );

        return without(selectedTournamentIds, ...itemsToRemove);
    }

    if (isAllTournamentsChecked) {
        const removableItems = itemTournamentIds.filter((id: string) =>
            isGroupCheckClicked ||
            isTournamentManuallyChecked ||
            hasSingleTournament
                ? checkedTournamentIds.includes(id)
                : !checkedTournamentIds.includes(id)
        );

        return without(selectedTournamentIds, ...removableItems);
    }

    const isTournamentsAlreadyChecked = checkedTournamentIds.every(id =>
        selectedTournamentIds.includes(id)
    );

    if (
        (isGroupCheckClicked && hasAnyCheckedTournament) ||
        isTournamentsAlreadyChecked
    ) {
        return without(selectedTournamentIds, ...checkedTournamentIds);
    }

    return selectedTournamentIds.concat(checkedTournamentIds);
}

export default useCategorizerRowChecker;
